import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { 
  Search, 
  Plus, 
  Pencil, 
  UserX,
  UserCheck,
  Trash2,
  ChevronLeft, 
  ChevronRight,
  Loader2,
  Filter,
  LogOut,
  Calendar,
  Download,
  FileSpreadsheet,
  FileText,
  SlidersHorizontal,
  X,
  CheckSquare,
  Square,
  MoreHorizontal,
  Building2,
  MapPin,
  Key,
  User,
  Mail,
  Phone,
  BadgeCheck,
  Copy,
  Link2,
  Eye,
  EyeOff
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Checkbox } from '../components/ui/checkbox';

export default function EmployeesPage() {
  const { isAdmin } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [sites, setSites] = useState([]);
  const [productionLines, setProductionLines] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    departmentId: '',
    siteId: '',
    status: '',
    sexe: '',
    typeContrat: '',
    categorieProfessionnelle: '',
    productionLine: '',
    shift: ''
  });
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0
  });
  const [deactivateDialog, setDeactivateDialog] = useState({ open: false, employee: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, employee: null });
  const [exitDialog, setExitDialog] = useState({ open: false, employee: null });
  const [exitData, setExitData] = useState({
    dateSortie: new Date().toISOString().split('T')[0],
    motifSortie: ''
  });
  
  // Sélection et actions groupées
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [bulkActionDialog, setBulkActionDialog] = useState({ open: false, action: '', title: '' });
  const [bulkActionData, setBulkActionData] = useState({ departmentId: '', siteId: '' });
  
  // Export avec sélection de colonnes
  const [exportDialog, setExportDialog] = useState({ open: false, format: '' });
  const [exportColumns, setExportColumns] = useState({
    // Informations de base
    matricule: true,
    firstName: true,
    lastName: true,
    email: true,
    phone: true,
    // Poste
    jobTitle: true,
    department: true,
    site: true,
    // RH
    status: true,
    hireDate: true,
    typeContrat: true,
    categorieProfessionnelle: true,
    // Personnel
    sexe: true,
    dateNaissance: false,
    situationFamiliale: false,
    // Sortie
    dateSortie: false,
    motifSortie: false,
    // Relations
    manager: false,
    // Production (ouvriers)
    productionLine: false,
    shift: false
  });
  
  // Définition des colonnes disponibles pour l'export
  const availableExportColumns = [
    { key: 'matricule', label: 'Matricule', category: 'Base' },
    { key: 'firstName', label: 'Prénom', category: 'Base' },
    { key: 'lastName', label: 'Nom', category: 'Base' },
    { key: 'email', label: 'Email', category: 'Base' },
    { key: 'phone', label: 'Téléphone', category: 'Base' },
    { key: 'jobTitle', label: 'Poste', category: 'Organisation' },
    { key: 'department', label: 'Département', category: 'Organisation' },
    { key: 'site', label: 'Site', category: 'Organisation' },
    { key: 'manager', label: 'Manager', category: 'Organisation' },
    { key: 'status', label: 'Statut', category: 'RH' },
    { key: 'hireDate', label: "Date d'embauche", category: 'RH' },
    { key: 'typeContrat', label: 'Type contrat', category: 'RH' },
    { key: 'categorieProfessionnelle', label: 'Catégorie', category: 'RH' },
    { key: 'sexe', label: 'Sexe', category: 'Personnel' },
    { key: 'dateNaissance', label: 'Date de naissance', category: 'Personnel' },
    { key: 'situationFamiliale', label: 'Situation familiale', category: 'Personnel' },
    { key: 'dateSortie', label: 'Date de sortie', category: 'Sortie' },
    { key: 'motifSortie', label: 'Motif de sortie', category: 'Sortie' },
    { key: 'productionLine', label: 'Ligne de production', category: 'Production' },
    { key: 'shift', label: 'Shift', category: 'Production' }
  ];
  
  // User account management
  const [userAccountDialog, setUserAccountDialog] = useState({ open: false, employee: null });
  const [userAccountMode, setUserAccountMode] = useState('create'); // 'create' or 'link'
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [newUserData, setNewUserData] = useState({ password: '', role: 'Employé' });
  const [userAccountLoading, setUserAccountLoading] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    // Charger départements, sites, lignes de production et shifts en parallèle
    Promise.all([fetchDepartments(), fetchSites(), fetchProductionLines(), fetchShifts()]);
  }, []);

  useEffect(() => {
    fetchEmployees();
  }, [search, filters, pagination.page]);

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      params.append('limit', 10);
      if (search) params.append('search', search);
      if (filters.departmentId) params.append('departmentId', filters.departmentId);
      if (filters.siteId) params.append('siteId', filters.siteId);
      if (filters.status) params.append('status', filters.status);
      // Filtres avancés
      if (filters.sexe) params.append('sexe', filters.sexe);
      if (filters.typeContrat) params.append('typeContrat', filters.typeContrat);
      if (filters.categorieProfessionnelle) params.append('categorieProfessionnelle', filters.categorieProfessionnelle);
      if (filters.productionLine) params.append('productionLine', filters.productionLine);
      if (filters.shift) params.append('shift', filters.shift);

      const response = await api.get(`/employees?${params.toString()}`);
      setEmployees(response.data.employees);
      setPagination(prev => ({
        ...prev,
        totalPages: response.data.totalPages,
        total: response.data.total
      }));
    } catch (error) {
      toast.error('Erreur lors du chargement des employés');
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await api.get('/departments');
      setDepartments(response.data.departments || response.data || []);
    } catch (error) {
      setDepartments([]);
    }
  };

  const fetchSites = async () => {
    try {
      const response = await api.get('/sites');
      setSites(response.data || []);
    } catch (error) {
      setSites([]);
    }
  };

  const fetchProductionLines = async () => {
    try {
      const response = await api.get('/production-lines');
      setProductionLines(response.data?.productionLines || []);
    } catch (error) {
      setProductionLines([]);
    }
  };

  const fetchShifts = async () => {
    try {
      const response = await api.get('/shifts');
      setShifts(response.data?.shifts || []);
    } catch (error) {
      setShifts([]);
    }
  };

  const handleDeactivate = async () => {
    if (!deactivateDialog.employee) return;
    
    try {
      await api.delete(`/employees/${deactivateDialog.employee.id}`);
      toast.success('Employé désactivé');
      fetchEmployees();
    } catch (error) {
      toast.error('Erreur lors de la désactivation');
    } finally {
      setDeactivateDialog({ open: false, employee: null });
    }
  };

  const handleDeclareExit = async () => {
    if (!exitDialog.employee || !exitData.motifSortie) {
      toast.error('Veuillez sélectionner un motif de sortie');
      return;
    }
    
    try {
      await api.post(`/employees/${exitDialog.employee.id}/exit`, {
        dateSortie: exitData.dateSortie,
        motifSortie: exitData.motifSortie
      });
      toast.success(`Sortie enregistrée : ${exitData.motifSortie}`);
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la déclaration de sortie');
    } finally {
      setExitDialog({ open: false, employee: null });
      setExitData({ dateSortie: new Date().toISOString().split('T')[0], motifSortie: '' });
    }
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      // Build query params from current filters
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filters.departmentId) params.append('departmentId', filters.departmentId);
      if (filters.siteId) params.append('siteId', filters.siteId);
      if (filters.status) params.append('status', filters.status);
      if (filters.sexe) params.append('sexe', filters.sexe);
      if (filters.typeContrat) params.append('typeContrat', filters.typeContrat);
      if (filters.categorieProfessionnelle) params.append('categorieProfessionnelle', filters.categorieProfessionnelle);
      if (filters.productionLine) params.append('productionLine', filters.productionLine);
      if (filters.shift) params.append('shift', filters.shift);
      params.append('format', format);
      
      // Add selected columns
      const selectedColumns = Object.entries(exportColumns)
        .filter(([_, isSelected]) => isSelected)
        .map(([key]) => key);
      params.append('columns', selectedColumns.join(','));

      const response = await api.get(`/employees/export?${params.toString()}`, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const extension = format === 'excel' ? 'xlsx' : 'pdf';
      link.setAttribute('download', `employes_${new Date().toISOString().split('T')[0]}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Export ${format.toUpperCase()} téléchargé`);
      setExportDialog({ open: false, format: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || `Erreur lors de l'export ${format}`);
    } finally {
      setExporting(false);
    }
  };

  const openExportDialog = (format) => {
    setExportDialog({ open: true, format });
  };

  const toggleAllExportColumns = (selected) => {
    setExportColumns(prev => {
      const newColumns = {};
      Object.keys(prev).forEach(key => {
        newColumns[key] = selected;
      });
      return newColumns;
    });
  };

  const toggleExportColumnsByCategory = (category, selected) => {
    setExportColumns(prev => {
      const newColumns = { ...prev };
      availableExportColumns
        .filter(col => col.category === category)
        .forEach(col => {
          newColumns[col.key] = selected;
        });
      return newColumns;
    });
  };

  const selectedColumnsCount = Object.values(exportColumns).filter(v => v).length;

  const clearFilters = () => {
    setFilters({
      departmentId: '',
      siteId: '',
      status: '',
      sexe: '',
      typeContrat: '',
      categorieProfessionnelle: '',
      productionLine: '',
      shift: ''
    });
    setSearch('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const activeFiltersCount = Object.values(filters).filter(v => v !== '').length + (search ? 1 : 0);

  // Fonctions de sélection
  const toggleSelectAll = () => {
    if (selectedEmployees.length === employees.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(employees.map(e => e.id));
    }
  };

  const toggleSelectEmployee = (employeeId) => {
    setSelectedEmployees(prev => 
      prev.includes(employeeId) 
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const isAllSelected = employees.length > 0 && selectedEmployees.length === employees.length;
  const isSomeSelected = selectedEmployees.length > 0 && selectedEmployees.length < employees.length;

  // Actions groupées
  const handleBulkAction = async (action, data = null) => {
    if (selectedEmployees.length === 0) {
      toast.error('Aucun employé sélectionné');
      return;
    }

    setBulkActionLoading(true);
    try {
      const response = await api.post('/employees/bulk-action', {
        employeeIds: selectedEmployees,
        action,
        data
      });
      
      const { results } = response.data;
      if (results.success > 0) {
        toast.success(`${results.success} employé(s) traité(s) avec succès`);
      }
      if (results.failed > 0) {
        toast.warning(`${results.failed} employé(s) non traité(s)`);
      }
      
      setSelectedEmployees([]);
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'action groupée');
    } finally {
      setBulkActionLoading(false);
      setBulkActionDialog({ open: false, action: '', title: '' });
    }
  };

  const openBulkActionDialog = (action, title) => {
    setBulkActionDialog({ open: true, action, title });
  };

  const handleReactivate = async (employee) => {
    try {
      await api.post(`/employees/${employee.id}/reactivate`);
      toast.success('Employé réactivé');
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réactivation');
    }
  };

  // User account management functions
  const generatePassword = () => {
    const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
    let password = '';
    for (let i = 0; i < 10; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  const openUserAccountDialog = async (employee) => {
    setUserAccountDialog({ open: true, employee });
    setUserAccountMode('create');
    const pwd = generatePassword();
    setGeneratedPassword(pwd);
    setNewUserData({ password: pwd, role: 'Employé' });
    setSelectedUserId('');
    setShowPassword(false);
    
    // Fetch available users for linking
    try {
      const response = await api.get('/users/available-for-linking');
      setAvailableUsers(response.data || []);
    } catch (error) {
      setAvailableUsers([]);
    }
  };

  const handleCreateUserAccount = async () => {
    const employee = userAccountDialog.employee;
    if (!employee) return;
    
    // Au moins un identifiant requis : email, téléphone OU matricule
    if (!employee.email && !employee.phone && !employee.matricule) {
      toast.error("Cet employé n'a aucun identifiant (email, téléphone ou matricule). Ajoutez-en un dans sa fiche.");
      return;
    }
    
    setUserAccountLoading(true);
    try {
      await api.post('/users/create-for-employee', {
        employeeId: employee.id,
        email: employee.email || null,
        phone: employee.phone || null,
        matricule: employee.matricule || null,
        firstName: employee.firstName,
        lastName: employee.lastName,
        password: newUserData.password,
        role: newUserData.role
      });
      
      toast.success('Compte utilisateur créé avec succès !');
      setUserAccountDialog({ open: false, employee: null });
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du compte');
    } finally {
      setUserAccountLoading(false);
    }
  };

  const handleLinkUserAccount = async () => {
    const employee = userAccountDialog.employee;
    if (!employee || !selectedUserId) return;
    
    setUserAccountLoading(true);
    try {
      await api.post('/users/link-to-employee', {
        employeeId: employee.id,
        userId: selectedUserId
      });
      
      toast.success('Compte lié avec succès !');
      setUserAccountDialog({ open: false, employee: null });
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la liaison du compte');
    } finally {
      setUserAccountLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié dans le presse-papier');
  };

  const handleDeletePermanently = async () => {
    if (!deleteDialog.employee) return;
    
    try {
      await api.delete(`/employees/${deleteDialog.employee.id}/permanent`);
      toast.success('Employé supprimé définitivement');
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeleteDialog({ open: false, employee: null });
    }
  };

  const getDepartmentName = (deptId) => {
    const dept = departments.find(d => d.id === deptId);
    return dept?.name || '-';
  };

  const getSiteName = (siteId) => {
    const site = sites.find(s => s.id === siteId);
    return site?.name || '-';
  };

  const getInitials = (employee) => {
    return `${employee.firstName?.[0] || ''}${employee.lastName?.[0] || ''}`.toUpperCase();
  };

  return (
    <DashboardLayout title="Employés">
      <div className="space-y-6" data-testid="employees-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">
              Liste des employés
            </h1>
            <p className="text-slate-600">
              {pagination.total} employé{pagination.total > 1 ? 's' : ''} au total
            </p>
          </div>
          
          {isAdmin && (
            <Link to="/employees/new">
              <Button className="bg-webtech-blue hover:bg-webtech-blue-hover text-white" data-testid="add-employee-btn">
                <Plus className="w-4 h-4 mr-2" />
                Ajouter un employé
              </Button>
            </Link>
          )}
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4 space-y-4">
            {/* Ligne principale : Recherche + Filtres de base + Actions */}
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Rechercher par nom, email, poste, matricule..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              
              <div className="flex flex-wrap gap-2">
                <Select
                  value={filters.departmentId || "all"}
                  onValueChange={(value) => {
                    setFilters(prev => ({ ...prev, departmentId: value === 'all' ? '' : value }));
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                >
                  <SelectTrigger className="w-[160px]" data-testid="department-filter">
                    <SelectValue placeholder="Département" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous départements</SelectItem>
                    {departments.map((dept) => (
                      <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={filters.status || "all"}
                  onValueChange={(value) => {
                    setFilters(prev => ({ ...prev, status: value === 'all' ? '' : value }));
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                >
                  <SelectTrigger className="w-[120px]" data-testid="status-filter">
                    <SelectValue placeholder="Statut" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    <SelectItem value="Actif">✅ Actif</SelectItem>
                    <SelectItem value="Inactif">⏸️ Inactif</SelectItem>
                    <SelectItem value="Sorti">🚪 Sorti</SelectItem>
                  </SelectContent>
                </Select>

                {/* Bouton Filtres avancés */}
                <Button
                  variant="outline"
                  onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                  className={showAdvancedFilters ? 'bg-blue-50 border-blue-300' : ''}
                >
                  <SlidersHorizontal className="w-4 h-4 mr-2" />
                  Filtres
                  {activeFiltersCount > 0 && (
                    <Badge className="ml-2 bg-blue-600">{activeFiltersCount}</Badge>
                  )}
                </Button>

                {/* Boutons Export */}
                <div className="flex gap-1 border-l pl-2 ml-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openExportDialog('excel')}
                    disabled={exporting}
                    className="text-green-700 border-green-200 hover:bg-green-50"
                    title="Exporter en Excel"
                    data-testid="export-excel-btn"
                  >
                    {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
                    <span className="hidden sm:inline ml-1">Excel</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openExportDialog('pdf')}
                    disabled={exporting}
                    className="text-red-700 border-red-200 hover:bg-red-50"
                    title="Exporter en PDF"
                    data-testid="export-pdf-btn"
                  >
                    {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                    <span className="hidden sm:inline ml-1">PDF</span>
                  </Button>
                </div>
              </div>
            </div>

            {/* Filtres avancés (repliables) */}
            {showAdvancedFilters && (
              <div className="pt-4 border-t border-slate-200 animate-in slide-in-from-top-2 duration-200">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-medium text-slate-700">Filtres avancés</p>
                  {activeFiltersCount > 0 && (
                    <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500 h-7">
                      <X className="w-3 h-3 mr-1" />
                      Réinitialiser
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  <Select
                    value={filters.siteId || "all"}
                    onValueChange={(value) => {
                      setFilters(prev => ({ ...prev, siteId: value === 'all' ? '' : value }));
                      setPagination(prev => ({ ...prev, page: 1 }));
                    }}
                  >
                    <SelectTrigger data-testid="site-filter">
                      <SelectValue placeholder="Site" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les sites</SelectItem>
                      {sites.map((site) => (
                        <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select
                    value={filters.sexe || "all"}
                    onValueChange={(value) => {
                      setFilters(prev => ({ ...prev, sexe: value === 'all' ? '' : value }));
                      setPagination(prev => ({ ...prev, page: 1 }));
                    }}
                  >
                    <SelectTrigger data-testid="sexe-filter">
                      <SelectValue placeholder="Sexe" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous</SelectItem>
                      <SelectItem value="Homme">👨 Homme</SelectItem>
                      <SelectItem value="Femme">👩 Femme</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select
                    value={filters.typeContrat || "all"}
                    onValueChange={(value) => {
                      setFilters(prev => ({ ...prev, typeContrat: value === 'all' ? '' : value }));
                      setPagination(prev => ({ ...prev, page: 1 }));
                    }}
                  >
                    <SelectTrigger data-testid="contrat-filter">
                      <SelectValue placeholder="Type contrat" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous contrats</SelectItem>
                      <SelectItem value="CDI">📋 CDI</SelectItem>
                      <SelectItem value="CDD">📄 CDD</SelectItem>
                      <SelectItem value="Intérim">⏱️ Intérim</SelectItem>
                      <SelectItem value="Stage">🎓 Stage</SelectItem>
                      <SelectItem value="Alternance">📚 Alternance</SelectItem>
                      <SelectItem value="Freelance">💼 Freelance</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select
                    value={filters.categorieProfessionnelle || "all"}
                    onValueChange={(value) => {
                      setFilters(prev => ({ ...prev, categorieProfessionnelle: value === 'all' ? '' : value }));
                      setPagination(prev => ({ ...prev, page: 1 }));
                    }}
                  >
                    <SelectTrigger data-testid="categorie-filter">
                      <SelectValue placeholder="Catégorie" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes catégories</SelectItem>
                      <SelectItem value="Cadre">👔 Cadre</SelectItem>
                      <SelectItem value="Agent de maîtrise">🔧 Agent de maîtrise</SelectItem>
                      <SelectItem value="Employé">👤 Employé</SelectItem>
                      <SelectItem value="Ouvrier">🛠️ Ouvrier</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Filtres spécifiques ouvriers : Ligne de production + Shift */}
                  <Select
                    value={filters.productionLine || "all"}
                    onValueChange={(value) => {
                      setFilters(prev => ({ ...prev, productionLine: value === 'all' ? '' : value }));
                      setPagination(prev => ({ ...prev, page: 1 }));
                    }}
                  >
                    <SelectTrigger data-testid="production-line-filter">
                      <SelectValue placeholder="Ligne de production" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes lignes</SelectItem>
                      {productionLines.length === 0 ? (
                        <div className="px-2 py-1.5 text-xs text-slate-400">Aucune ligne configurée</div>
                      ) : (
                        productionLines.map(pl => (
                          <SelectItem key={pl.id} value={pl.id}>🏭 {pl.name}</SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>

                  <Select
                    value={filters.shift || "all"}
                    onValueChange={(value) => {
                      setFilters(prev => ({ ...prev, shift: value === 'all' ? '' : value }));
                      setPagination(prev => ({ ...prev, page: 1 }));
                    }}
                  >
                    <SelectTrigger data-testid="shift-filter">
                      <SelectValue placeholder="Shift" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous shifts</SelectItem>
                      {shifts.length === 0 ? (
                        <div className="px-2 py-1.5 text-xs text-slate-400">Aucun shift configuré</div>
                      ) : (
                        shifts.map(s => (
                          <SelectItem key={s.id} value={s.id}>⏰ {s.name}</SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Barre d'actions groupées */}
        {selectedEmployees.length > 0 && (
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="py-3">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <CheckSquare className="w-5 h-5 text-blue-600" />
                  <span className="font-medium text-blue-900">
                    {selectedEmployees.length} employé{selectedEmployees.length > 1 ? 's' : ''} sélectionné{selectedEmployees.length > 1 ? 's' : ''}
                  </span>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedEmployees([])} className="text-blue-600 hover:text-blue-800">
                    Désélectionner
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" disabled={bulkActionLoading}>
                        {bulkActionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <MoreHorizontal className="w-4 h-4 mr-2" />}
                        Actions groupées
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleBulkAction('deactivate')}>
                        <UserX className="w-4 h-4 mr-2 text-amber-600" />
                        Désactiver
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleBulkAction('activate')}>
                        <UserCheck className="w-4 h-4 mr-2 text-green-600" />
                        Réactiver
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => openBulkActionDialog('changeDepartment', 'Changer de département')}>
                        <Building2 className="w-4 h-4 mr-2 text-blue-600" />
                        Changer département
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openBulkActionDialog('changeSite', 'Changer de site')}>
                        <MapPin className="w-4 h-4 mr-2 text-purple-600" />
                        Changer site
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        onClick={() => openBulkActionDialog('delete', 'Supprimer définitivement')}
                        className="text-red-600"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Supprimer définitivement
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Table */}
        <Card>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {isAdmin && (
                    <TableHead className="w-[50px]">
                      <Checkbox
                        checked={isAllSelected}
                        onCheckedChange={toggleSelectAll}
                        className={isSomeSelected ? 'data-[state=checked]:bg-blue-400' : ''}
                      />
                    </TableHead>
                  )}
                  <TableHead>Employé</TableHead>
                  <TableHead>Poste</TableHead>
                  <TableHead className="hidden md:table-cell">Département</TableHead>
                  <TableHead className="hidden lg:table-cell">Site</TableHead>
                  <TableHead className="hidden lg:table-cell">Contact</TableHead>
                  <TableHead>Statut</TableHead>
                  {isAdmin && <TableHead className="w-[80px] text-center">Compte</TableHead>}
                  {isAdmin && <TableHead className="w-[100px]">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={isAdmin ? 9 : 6} className="text-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin mx-auto text-webtech-blue" />
                    </TableCell>
                  </TableRow>
                ) : employees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isAdmin ? 9 : 6} className="text-center py-12">
                      <div className="space-y-3">
                        <p className="text-slate-500">Aucun employé trouvé</p>
                        {departments.length === 0 && (
                          <p className="text-sm text-amber-600">
                            💡 Conseil : Commencez par créer des départements et sites dans les <a href="/settings" className="underline font-medium">Paramètres</a> avant d'ajouter des employés.
                          </p>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  employees.map((employee) => (
                    <TableRow 
                      key={employee.id} 
                      data-testid={`employee-row-${employee.id}`}
                      className={selectedEmployees.includes(employee.id) ? 'bg-blue-50' : ''}
                    >
                      {isAdmin && (
                        <TableCell>
                          <Checkbox
                            checked={selectedEmployees.includes(employee.id)}
                            onCheckedChange={() => toggleSelectEmployee(employee.id)}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10">
                            {employee.photoUrl ? (
                              <AvatarImage src={employee.photoUrl} alt={`${employee.firstName} ${employee.lastName}`} />
                            ) : null}
                            <AvatarFallback className="bg-webtech-blue text-white">
                              {getInitials(employee)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium text-slate-900">
                              {employee.firstName} {employee.lastName}
                            </p>
                            <p className="text-sm text-slate-500 md:hidden">
                              {employee.email || '-'}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {employee.jobTitle}
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-slate-600">
                        {getDepartmentName(employee.departmentId)}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-slate-600">
                        {getSiteName(employee.siteId)}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <div className="text-sm">
                          <p className="text-slate-600">{employee.email || '-'}</p>
                          {employee.phone && (
                            <p className="text-slate-500">{employee.phone}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant={employee.status === 'Actif' ? 'default' : 'secondary'}
                          className={employee.status === 'Actif' 
                            ? 'bg-green-100 text-green-700 hover:bg-green-100' 
                            : 'bg-slate-100 text-slate-600'
                          }
                        >
                          {employee.status}
                        </Badge>
                      </TableCell>
                      {isAdmin && (
                        <TableCell className="text-center">
                          {employee.userId ? (
                            <div className="flex justify-center" title="Compte utilisateur actif">
                              <UserCheck className="w-5 h-5 text-green-600" />
                            </div>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-slate-400 hover:text-blue-600 hover:bg-blue-50"
                              onClick={() => openUserAccountDialog(employee)}
                              title="Créer ou lier un compte utilisateur"
                              data-testid={`create-account-${employee.id}`}
                            >
                              <UserX className="w-5 h-5" />
                            </Button>
                          )}
                        </TableCell>
                      )}
                      {isAdmin && (
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Link to={`/employees/${employee.id}/edit`}>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`edit-employee-${employee.id}`}>
                                <Pencil className="w-4 h-4" />
                              </Button>
                            </Link>
                            {employee.status === 'Actif' ? (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-8 w-8 p-0 text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                                onClick={() => setExitDialog({ open: true, employee })}
                                data-testid={`exit-employee-${employee.id}`}
                                title="Déclarer une sortie"
                              >
                                <LogOut className="w-4 h-4" />
                              </Button>
                            ) : (
                              <>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                                  onClick={() => handleReactivate(employee)}
                                  data-testid={`reactivate-employee-${employee.id}`}
                                  title="Réactiver"
                                >
                                  <UserCheck className="w-4 h-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => setDeleteDialog({ open: true, employee })}
                                  data-testid={`delete-employee-${employee.id}`}
                                  title="Supprimer définitivement"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
              <p className="text-sm text-slate-600">
                Page {pagination.page} sur {pagination.totalPages}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === pagination.totalPages}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* Deactivate Dialog */}
        <AlertDialog open={deactivateDialog.open} onOpenChange={(open) => setDeactivateDialog({ open, employee: null })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Désactiver cet employé ?</AlertDialogTitle>
              <AlertDialogDescription>
                L'employé <strong>{deactivateDialog.employee?.firstName} {deactivateDialog.employee?.lastName}</strong> sera 
                marqué comme inactif. Cette action peut être annulée.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDeactivate}
                className="bg-red-600 hover:bg-red-700"
              >
                Désactiver
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete Permanently Dialog */}
        <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, employee: null })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="text-red-600">Supprimer définitivement cet employé ?</AlertDialogTitle>
              <AlertDialogDescription className="space-y-2">
                <p>
                  L'employé <strong>{deleteDialog.employee?.firstName} {deleteDialog.employee?.lastName}</strong> sera 
                  supprimé définitivement de la base de données.
                </p>
                <p className="text-red-600 font-medium">
                  ⚠️ Cette action est irréversible et supprimera également son compte utilisateur s'il en possède un.
                </p>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDeletePermanently}
                className="bg-red-600 hover:bg-red-700"
              >
                Supprimer définitivement
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Exit Declaration Dialog */}
        <Dialog open={exitDialog.open} onOpenChange={(open) => {
          setExitDialog({ open, employee: open ? exitDialog.employee : null });
          if (!open) setExitData({ dateSortie: new Date().toISOString().split('T')[0], motifSortie: '' });
        }}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <LogOut className="w-5 h-5 text-orange-600" />
                Déclarer une sortie
              </DialogTitle>
              <DialogDescription>
                Enregistrez le départ de <strong>{exitDialog.employee?.firstName} {exitDialog.employee?.lastName}</strong>.
                Cette information sera utilisée pour le calcul des KPI RH.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="dateSortie" className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Date de sortie <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="dateSortie"
                  type="date"
                  value={exitData.dateSortie}
                  onChange={(e) => setExitData(prev => ({ ...prev, dateSortie: e.target.value }))}
                  className="h-11"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  Motif de sortie <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={exitData.motifSortie || "placeholder"}
                  onValueChange={(value) => setExitData(prev => ({ 
                    ...prev, 
                    motifSortie: value === "placeholder" ? "" : value 
                  }))}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Sélectionner le motif..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="placeholder" disabled>Sélectionner le motif...</SelectItem>
                    <SelectItem value="Démission">🚶 Démission</SelectItem>
                    <SelectItem value="Licenciement">⚠️ Licenciement</SelectItem>
                    <SelectItem value="Fin de CDD">📄 Fin de CDD</SelectItem>
                    <SelectItem value="Fin de période d'essai">🔍 Fin de période d'essai</SelectItem>
                    <SelectItem value="Retraite">🎉 Retraite</SelectItem>
                    <SelectItem value="Transfert sortant">🔄 Transfert sortant (mutation)</SelectItem>
                    <SelectItem value="Rupture conventionnelle">🤝 Rupture conventionnelle</SelectItem>
                    <SelectItem value="Décès">🕯️ Décès</SelectItem>
                    <SelectItem value="Autre">📝 Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-800">
                  💡 <strong>Impact KPI :</strong> Cette sortie sera comptabilisée dans le calcul du turnover 
                  et des indicateurs de départ du mois.
                </p>
              </div>
            </div>
            
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setExitDialog({ open: false, employee: null })}
              >
                Annuler
              </Button>
              <Button 
                onClick={handleDeclareExit}
                className="bg-orange-600 hover:bg-orange-700"
                disabled={!exitData.motifSortie}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Enregistrer la sortie
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Action Dialog */}
        <Dialog open={bulkActionDialog.open} onOpenChange={(open) => {
          setBulkActionDialog({ open, action: open ? bulkActionDialog.action : '', title: open ? bulkActionDialog.title : '' });
          if (!open) setBulkActionData({ departmentId: '', siteId: '' });
        }}>
          <DialogContent className="sm:max-w-[450px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {bulkActionDialog.action === 'changeDepartment' && <Building2 className="w-5 h-5 text-blue-600" />}
                {bulkActionDialog.action === 'changeSite' && <MapPin className="w-5 h-5 text-purple-600" />}
                {bulkActionDialog.action === 'delete' && <Trash2 className="w-5 h-5 text-red-600" />}
                {bulkActionDialog.title}
              </DialogTitle>
              <DialogDescription>
                Action sur <strong>{selectedEmployees.length}</strong> employé{selectedEmployees.length > 1 ? 's' : ''} sélectionné{selectedEmployees.length > 1 ? 's' : ''}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {bulkActionDialog.action === 'changeDepartment' && (
                <div className="space-y-2">
                  <Label>Nouveau département</Label>
                  <Select
                    value={bulkActionData.departmentId || "placeholder"}
                    onValueChange={(value) => setBulkActionData(prev => ({ ...prev, departmentId: value === "placeholder" ? "" : value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un département..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="placeholder" disabled>Sélectionner un département...</SelectItem>
                      {departments.map((dept) => (
                        <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {bulkActionDialog.action === 'changeSite' && (
                <div className="space-y-2">
                  <Label>Nouveau site</Label>
                  <Select
                    value={bulkActionData.siteId || "placeholder"}
                    onValueChange={(value) => setBulkActionData(prev => ({ ...prev, siteId: value === "placeholder" ? "" : value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un site..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="placeholder" disabled>Sélectionner un site...</SelectItem>
                      {sites.map((site) => (
                        <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {bulkActionDialog.action === 'delete' && (
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-sm text-red-800">
                    ⚠️ <strong>Attention :</strong> Cette action est irréversible. Seuls les employés 
                    <strong> inactifs</strong> seront supprimés définitivement. Les employés actifs seront ignorés.
                  </p>
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setBulkActionDialog({ open: false, action: '', title: '' })}
              >
                Annuler
              </Button>
              <Button 
                onClick={() => {
                  if (bulkActionDialog.action === 'changeDepartment') {
                    handleBulkAction('changeDepartment', { departmentId: bulkActionData.departmentId });
                  } else if (bulkActionDialog.action === 'changeSite') {
                    handleBulkAction('changeSite', { siteId: bulkActionData.siteId });
                  } else if (bulkActionDialog.action === 'delete') {
                    handleBulkAction('delete');
                  }
                }}
                disabled={
                  bulkActionLoading ||
                  (bulkActionDialog.action === 'changeDepartment' && !bulkActionData.departmentId) ||
                  (bulkActionDialog.action === 'changeSite' && !bulkActionData.siteId)
                }
                className={bulkActionDialog.action === 'delete' ? 'bg-red-600 hover:bg-red-700' : ''}
              >
                {bulkActionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Confirmer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* User Account Management Dialog */}
        <Dialog 
          open={userAccountDialog.open} 
          onOpenChange={(open) => {
            if (!open) {
              setUserAccountDialog({ open: false, employee: null });
              setGeneratedPassword('');
            }
          }}
        >
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Key className="w-5 h-5 text-blue-600" />
                Gestion du compte utilisateur
              </DialogTitle>
              <DialogDescription>
                {userAccountDialog.employee && (
                  <span>
                    Pour <strong>{userAccountDialog.employee.firstName} {userAccountDialog.employee.lastName}</strong>
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Mode Selection */}
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setUserAccountMode('create')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    userAccountMode === 'create' 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <Plus className={`w-6 h-6 mx-auto mb-2 ${userAccountMode === 'create' ? 'text-blue-600' : 'text-slate-400'}`} />
                  <span className={`text-sm font-medium ${userAccountMode === 'create' ? 'text-blue-900' : 'text-slate-600'}`}>
                    Créer un compte
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setUserAccountMode('link')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    userAccountMode === 'link' 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <Link2 className={`w-6 h-6 mx-auto mb-2 ${userAccountMode === 'link' ? 'text-blue-600' : 'text-slate-400'}`} />
                  <span className={`text-sm font-medium ${userAccountMode === 'link' ? 'text-blue-900' : 'text-slate-600'}`}>
                    Lier un compte existant
                  </span>
                </button>
              </div>

              {userAccountMode === 'create' && (
                <div className="space-y-4">
                  {/* Identifiants disponibles */}
                  <div className="p-3 bg-slate-50 rounded-lg space-y-2">
                    <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                      Identifiant(s) de connexion
                    </p>
                    <div className="space-y-1.5 text-sm">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-slate-500 flex-shrink-0" />
                        <span className="text-slate-600 w-20">Email :</span>
                        <span className="font-medium text-slate-900 truncate">
                          {userAccountDialog.employee?.email || (
                            <span className="text-slate-400 italic">non défini</span>
                          )}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-slate-500 flex-shrink-0" />
                        <span className="text-slate-600 w-20">Téléphone :</span>
                        <span className="font-medium text-slate-900 truncate">
                          {userAccountDialog.employee?.phone || (
                            <span className="text-slate-400 italic">non défini</span>
                          )}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <BadgeCheck className="w-4 h-4 text-slate-500 flex-shrink-0" />
                        <span className="text-slate-600 w-20">Matricule :</span>
                        <span className="font-medium text-slate-900 truncate">
                          {userAccountDialog.employee?.matricule || (
                            <span className="text-slate-400 italic">non défini</span>
                          )}
                        </span>
                      </div>
                    </div>
                    {!(userAccountDialog.employee?.email || userAccountDialog.employee?.phone || userAccountDialog.employee?.matricule) ? (
                      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                        ⚠ Au moins un identifiant est requis (email, téléphone ou matricule). Ajoutez-en un dans la fiche de l'employé.
                      </p>
                    ) : (
                      <p className="text-xs text-emerald-700">
                        L'employé pourra se connecter avec n'importe lequel des identifiants ci-dessus.
                      </p>
                    )}
                  </div>

                  {/* Password */}
                  <div className="space-y-2">
                    <Label>Mot de passe temporaire</Label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Input
                          type={showPassword ? "text" : "password"}
                          value={newUserData.password}
                          onChange={(e) => setNewUserData(prev => ({ ...prev, password: e.target.value }))}
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => copyToClipboard(newUserData.password)}
                        title="Copier"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-slate-500">
                      Partagez ce mot de passe avec l'employé. Il pourra le modifier après connexion.
                    </p>
                  </div>

                  {/* Role */}
                  <div className="space-y-2">
                    <Label>Rôle</Label>
                    <Select
                      value={newUserData.role}
                      onValueChange={(value) => setNewUserData(prev => ({ ...prev, role: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Employé">👤 Employé</SelectItem>
                        <SelectItem value="Manager">👔 Manager</SelectItem>
                        <SelectItem value="Admin">🔑 Administrateur</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {userAccountMode === 'link' && (
                <div className="space-y-4">
                  {availableUsers.length > 0 ? (
                    <div className="space-y-2">
                      <Label>Sélectionner un compte existant</Label>
                      <Select
                        value={selectedUserId || "placeholder"}
                        onValueChange={(value) => setSelectedUserId(value === "placeholder" ? "" : value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Choisir un compte..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="placeholder" disabled>Choisir un compte...</SelectItem>
                          {availableUsers.map(user => (
                            <SelectItem key={user.id} value={user.id}>
                              <div className="flex items-center gap-2">
                                <User className="w-4 h-4 text-slate-400" />
                                <span>{user.firstName} {user.lastName}</span>
                                <span className="text-slate-400">({user.email})</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500">
                        Seuls les comptes non liés à d'autres employés sont affichés.
                      </p>
                    </div>
                  ) : (
                    <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <p className="text-sm text-amber-800">
                        ⚠️ Aucun compte disponible pour la liaison. Tous les comptes existants sont déjà liés à des employés.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setUserAccountDialog({ open: false, employee: null })}
              >
                Annuler
              </Button>
              <Button 
                onClick={userAccountMode === 'create' ? handleCreateUserAccount : handleLinkUserAccount}
                disabled={
                  userAccountLoading ||
                  (userAccountMode === 'create' && !(
                    userAccountDialog.employee?.email ||
                    userAccountDialog.employee?.phone ||
                    userAccountDialog.employee?.matricule
                  )) ||
                  (userAccountMode === 'link' && !selectedUserId)
                }
                className="bg-blue-600 hover:bg-blue-700"
              >
                {userAccountLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Key className="w-4 h-4 mr-2" />}
                {userAccountMode === 'create' ? 'Créer le compte' : 'Lier le compte'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Export Dialog avec sélection de colonnes */}
        <Dialog open={exportDialog.open} onOpenChange={(open) => setExportDialog({ open, format: open ? exportDialog.format : '' })}>
          <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {exportDialog.format === 'excel' ? (
                  <FileSpreadsheet className="w-5 h-5 text-green-600" />
                ) : (
                  <FileText className="w-5 h-5 text-red-600" />
                )}
                Exporter en {exportDialog.format === 'excel' ? 'Excel' : 'PDF'}
              </DialogTitle>
              <DialogDescription>
                Sélectionnez les colonnes à inclure dans l'export ({selectedColumnsCount} sélectionnées)
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Actions rapides */}
              <div className="flex items-center gap-2 pb-3 border-b">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => toggleAllExportColumns(true)}
                  className="text-xs"
                >
                  <CheckSquare className="w-3 h-3 mr-1" />
                  Tout sélectionner
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => toggleAllExportColumns(false)}
                  className="text-xs"
                >
                  <Square className="w-3 h-3 mr-1" />
                  Tout désélectionner
                </Button>
              </div>

              {/* Colonnes groupées par catégorie */}
              {['Base', 'Organisation', 'RH', 'Personnel', 'Production', 'Sortie'].map(category => {
                const categoryColumns = availableExportColumns.filter(col => col.category === category);
                const selectedInCategory = categoryColumns.filter(col => exportColumns[col.key]).length;
                const allSelectedInCategory = selectedInCategory === categoryColumns.length;
                
                return (
                  <div key={category} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => toggleExportColumnsByCategory(category, !allSelectedInCategory)}
                        className="flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-slate-900"
                      >
                        <Checkbox 
                          checked={allSelectedInCategory}
                          className="pointer-events-none"
                        />
                        <span>{category}</span>
                        <Badge variant="secondary" className="text-xs">
                          {selectedInCategory}/{categoryColumns.length}
                        </Badge>
                      </button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 pl-6">
                      {categoryColumns.map(col => (
                        <label 
                          key={col.key} 
                          className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer hover:text-slate-900"
                        >
                          <Checkbox
                            checked={exportColumns[col.key]}
                            onCheckedChange={(checked) => {
                              setExportColumns(prev => ({ ...prev, [col.key]: checked }));
                            }}
                          />
                          <span>{col.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}

              {/* Info sur les filtres appliqués */}
              {activeFiltersCount > 0 && (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800">
                    <Filter className="w-4 h-4 inline mr-1" />
                    <strong>{activeFiltersCount} filtre{activeFiltersCount > 1 ? 's' : ''}</strong> actif{activeFiltersCount > 1 ? 's' : ''} - L'export ne contiendra que les employés correspondants.
                  </p>
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setExportDialog({ open: false, format: '' })}
              >
                Annuler
              </Button>
              <Button 
                onClick={() => handleExport(exportDialog.format)}
                disabled={exporting || selectedColumnsCount === 0}
                className={exportDialog.format === 'excel' 
                  ? 'bg-green-600 hover:bg-green-700' 
                  : 'bg-red-600 hover:bg-red-700'
                }
              >
                {exporting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : exportDialog.format === 'excel' ? (
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                ) : (
                  <FileText className="w-4 h-4 mr-2" />
                )}
                Exporter ({selectedColumnsCount} colonnes)
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
